// src/components/tickets/detail/ThreadList.tsx
//
// The typed thread (Story 4.2 AC 6), chronological oldest-first:
//   * response      → on surface, with a quiet "Sent unedited from Yak's draft"
//                     / "Edited from Yak's draft" meta line when sent_unedited is
//                     non-null.
//   * internal_note → YELLOW-WALLED (yellow-50 bg, yellow-200 left border) +
//                     "Internal — not visible to rep" — must NEVER be confusable
//                     with a rep-visible reply at a glance.
//   * rep_reply     → attributed to the rep with timestamp; an orange advisory
//                     flag chip + "Sender doesn't match {rep name}'s registered
//                     address." when unverified_sender is true.
// Empty thread → the calm empty-state copy (all threads are empty until 4.3/4.6
// ship — expected, not a bug).
//
// SEAM: no composer here — Story 4.3 mounts Respond / Internal note under this.

import { AlertTriangle } from 'lucide-react'
import { formatDateTime } from '../../../utils/dateUtils'
import { messageKindMeta, personName } from '../../../utils/ticketDisplay'
import type { YakMessageWithAuthor } from '../../../types/yak'

interface ThreadListProps {
  messages: YakMessageWithAuthor[]
  // The call's rep name, for the unverified-sender advisory copy.
  repName: string | null
}

function sentUneditedLine(sentUnedited: boolean): string {
  return sentUnedited
    ? "Sent unedited from Yak's draft"
    : "Edited from Yak's draft"
}

export function ThreadList({ messages, repName }: ThreadListProps) {
  return (
    <section className="card p-6" aria-labelledby="thread-heading">
      <h2
        id="thread-heading"
        className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500"
      >
        Thread
      </h2>

      {messages.length === 0 ? (
        <p className="text-sm text-gray-500">
          No messages yet. Responses and internal notes will appear here.
        </p>
      ) : (
        <ul className="space-y-4">
          {messages.map(m => {
            const meta = messageKindMeta(m.kind)
            const when = m.created_at ? formatDateTime(m.created_at) : ''
            const who =
              m.kind === 'rep_reply'
                ? (repName ?? 'Rep')
                : m.author
                  ? personName(m.author)
                  : '—'

            if (meta.tone === 'internal') {
              return (
                <li
                  key={m.id}
                  className="rounded-md border-l-4 border-yellow-200 bg-yellow-50 p-3"
                >
                  <p className="text-xs font-semibold text-yellow-800">
                    {meta.label}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-900">
                    {m.body}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {who}
                    {when ? ` · ${when}` : ''}
                  </p>
                </li>
              )
            }

            return (
              <li key={m.id} className="rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <span className="font-medium text-gray-700">
                    {m.kind === 'rep_reply' ? `${who} (rep)` : who}
                  </span>
                  {when ? <span>· {when}</span> : null}
                </div>

                {m.kind === 'rep_reply' && m.unverified_sender ? (
                  <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    Sender doesn&apos;t match {repName ?? 'the rep'}&apos;s
                    registered address.
                  </p>
                ) : null}

                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-900">
                  {m.body}
                </p>

                {m.kind === 'response' && m.sent_unedited !== null ? (
                  <p className="mt-1 text-xs text-gray-400">
                    {sentUneditedLine(m.sent_unedited)}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {/* SEAM: Story 4.3 mounts the composer (Respond / Internal note) here. */}
    </section>
  )
}
